/*!
 * Portions of this code are derived from d3kit v3.2.0
 *
 * Copyright (c) 2015 Twitter, Inc.
 *
 * @license MIT
 *
 *
 * Portions of this code are derived from d3kit-timeline v2.0.1
 *
 * Copyright 2015 Krist Wongsuphasawat
 *
 * @license Apache-2.0
 *
 * NOTICE
 * -------
 *
 * This project includes code originally licensed under the Apache License, Version 2.0.
 *
 * Original Project: d3kit-timeline v2.0.1
 * Original Author: Krist Wongsuphasawat
 *
 * Modifications to the original code:
 * - Code has been inlined
 * - Unused options `keyFn`, `labella`, `labelTextColor`, `scale` have been removed
 * - Option `direction` has been restricted to "horizontal" and "vertical"
 * - Dependency to d3kit has been inlined
 *
 */

import { Transition } from "d3"
import { max } from "d3-array"
import { axisBottom, axisLeft, type Axis } from "d3-axis"
import { hsl, HSLColor } from "d3-color"
import { scaleTime } from "d3-scale"
import { BaseType, select, selectAll, Selection } from "d3-selection"

import "d3-transition"
import labella from "labella"
import debounce from "lodash.debounce"

import { Coloring, createColoring, createPalette } from "../color"
import { Renderer } from "../renderer"
import { Widget } from "../widget"

type GenericSelection = Selection<
  SVGGElement | SVGTextElement,
  any,
  any,
  undefined
>
type HistoryDirection = "horizontal" | "vertical"

interface HistoryEvent {
  id: number
  href: string

  type: string
  old?: boolean
  cat: number
  date: string
  text: string
  class: string
  desc?: string
  query: {
    id: number
    source: string
    url: string
  }
}

interface HistoryData {
  minDate: string
  maxDate: string
  event: HistoryEvent[]
  title: string
}

function setupColorPallete(rootColor: HSLColor, paletteSize: number) {
  const palette = createPalette(rootColor, paletteSize)
  const greyColoring = createColoring(hsl("#888888"))
  return function getEventColors(event: HistoryEvent): Coloring {
    return event.old ? greyColoring : palette[event.cat % paletteSize]
  }
}

export const history: Renderer = function (widget: Widget, data: HistoryData) {
  const historyData = data.event

  let id = 1
  historyData.forEach((event) => (event.id = id++))

  widget.container.innerHTML = ""

  const minDate = new Date(data.minDate)
  const maxDate = new Date(data.maxDate)

  const getEventColors = setupColorPallete(widget.getRootColor(), 12)
  const getEventStandardColor = (event: HistoryEvent) =>
    getEventColors(event).standard

  const options = {
    alternatingTickLength: 10,
    initialHeight: 50,
    initialWidth: 400,
    direction: "horizontal" as HistoryDirection,
    domain: [minDate, maxDate],
    dotRadius: 5,
    linkColor: getEventStandardColor,
    dotColor: getEventStandardColor,
    labelBgColor: getEventStandardColor,
    layerGap: 20,
    textStyle: { fill: "#fff" },
    margin: { left: 12, right: 15, top: 5, bottom: 35 },
    labelPadding: { left: 4, right: 4, top: 3, bottom: 2 },
    textYOffset: "0.85em",

    timeFn: (event: HistoryEventData) => new Date(event.date),
    textFn: (event: HistoryEventData) => event.text,
    formatAxis: (axis: Axis<Date>) => {
      const yearRange = maxDate.getFullYear() - minDate.getFullYear()
      return axis
        .tickFormat(widget.i18n().timeScaleFormatter)
        .tickSizeInner(8)
        .tickSizeOuter(10)
        .ticks(yearRange <= 4 ? 2 * yearRange : undefined)
    },
  }

  const dimension = {
    innerWidth: 0,
    innerHeight: 0,
    width: options.initialWidth,
    height: options.initialHeight,
  }

  type HistoryEventData = (typeof historyData)[0]
  let force = new labella.Force()
  const scale = scaleTime()
  const container = select(widget.container)

  // Enforce line-height = 0 to fix issue with height resizing
  // https://github.com/twitter/d3kit/issues/13
  container.style("line-height", 0)

  const chartRoot = container
    .append("div")
    .style("display", "inline-block")
    .style("position", "relative")
    .style("line-height", 0)
  const svg = chartRoot
    .append("svg")
    .style("position", "absolute")
    .style("top", 0)
    .style("left", 0)
  const rootG = svg.append("g")

  function createLayer(parent: GenericSelection, name: string) {
    return parent.append("g").attr("name", name)
  }

  const dummyLayer = createLayer(rootG, "dummy")
  const mainLayer = createLayer(rootG, "main")
  const axisLayer = createLayer(mainLayer, "main/axis")
  const linkLayer = createLayer(mainLayer, "main/link")
  const labelLayer = createLayer(mainLayer, "main/label")
  const dotLayer = createLayer(mainLayer, "main/dot")

  axisLayer.classed("axis", true)

  function fitToContainer() {
    const { width, height } = dimension
    const node = container.node()
    const containerWidth = node?.clientWidth ?? 0
    const containerHeight = node?.clientHeight ?? 0

    let needsUpdate = false
    // Always use full container width
    if (width !== containerWidth) {
      dimension.width = containerWidth
      needsUpdate = true
    }
    // Shrink height to container
    if (height > containerHeight) {
      dimension.height = containerHeight
      needsUpdate = true
    }
    if (needsUpdate) {
      updateDimension()
      updateDimension.flush()
    }
  }

  function resizeToFitNodes() {
    const nodes = force.nodes()
    let maxVal
    switch (options.direction) {
      case "horizontal":
        maxVal =
          max<labella.Node<HistoryEventData>, number>(nodes, (d) =>
            Math.abs(d.y)
          ) ?? 0
        dimension.height = maxVal + options.margin.top + options.margin.bottom
        break
      case "vertical":
        maxVal =
          max<labella.Node<HistoryEventData>, number>(nodes, (d) =>
            Math.abs(d.x + d.dx)
          ) ?? 0
        dimension.width = maxVal + options.margin.left + options.margin.right
        break
    }
  }

  const updateDimension = debounce(function updateDimension() {
    const { width, height } = dimension
    const { margin } = options
    const { top, right, bottom, left } = margin

    dimension.innerWidth = width - left - right
    dimension.innerHeight = height - top - bottom

    chartRoot.style("width", `${width}px`).style("height", `${height}px`)
    svg.attr("width", width).attr("height", height)
    rootG.attr("transform", `translate(${left}, ${top})`)

    render()
  }, 1)

  function updateLabelText(
    selection:
      | Selection<SVGTextElement, any, SVGGElement, unknown>
      | Transition<BaseType, any, SVGGElement, unknown>,
    accessor: (
      node: labella.Node<HistoryEventData>
    ) => HistoryEventData | HistoryEventData
  ) {
    const accessorFn =
      typeof accessor === "function" ? accessor : () => accessor
    selection
      .attr("dy", options.textYOffset)
      .attr("x", options.labelPadding.left)
      .attr("y", options.labelPadding.top)
      .style("fill", options.textStyle.fill)
      .text((d) => options.textFn(accessorFn(d)))
  }

  function drawAxes() {
    let axis
    switch (options.direction) {
      case "vertical":
        axis = axisLeft<Date>(scale)
        mainLayer.attr("transform", "translate(" + 0 + "," + 0 + ")")
        break
      case "horizontal":
        axis = axisBottom<Date>(scale)
        mainLayer.attr(
          "transform",
          "translate(" + 0 + "," + dimension.innerHeight + ")"
        )
        break
    }

    options.formatAxis?.(axis)
    axisLayer.call(axis).attr("font-family", null).attr("font-size", null)
  }

  function drawDots(data: HistoryEventData[]) {
    const field = options.direction === "vertical" ? "cy" : "cx"
    const timePos = (d: HistoryEventData) => scale(options.timeFn(d))

    const sUpdate = dotLayer.selectAll("circle.dot").data(data)
    sUpdate
      .enter()
      .append("circle")
      .classed("dot", true)
      .style("fill", options.dotColor)
      .attr("r", options.dotRadius)
      .attr(field, timePos)
    sUpdate
      .transition()
      .style("fill", options.dotColor)
      .attr("r", options.dotRadius)
      .attr(field, timePos)
    sUpdate.exit().remove()
  }

  function drawLabels(nodes: labella.Node<HistoryEventData>[]) {
    let nodeHeight
    if (options.direction === "vertical") {
      nodeHeight = max<labella.Node<HistoryEventData>, number>(
        nodes,
        (d) => d.w
      )
    } else {
      nodeHeight = max<labella.Node<HistoryEventData>, number>(
        nodes,
        (d) => d.h
      )
    }

    const renderer = new labella.Renderer({
      nodeHeight: nodeHeight ?? 0,
      layerGap: options.layerGap,
      direction: options.direction === "horizontal" ? "up" : "right",
    })

    renderer.layout(nodes)

    function nodePos(d: labella.Node<HistoryEventData>): string | null {
      if (isNaN(d.x) || isNaN(d.y)) {
        return null
      }
      switch (options.direction) {
        case "vertical":
          return "translate(" + d.x + "," + (d.y - d.dy / 2) + ")"
        case "horizontal":
          return "translate(" + (d.x - d.dx / 2) + "," + d.y + ")"
      }
    }

    const selection = labelLayer
      .selectAll<SVGGElement, labella.Node<HistoryEventData>>("g.label-g")
      .data(nodes)
    const sEnter = selection
      .enter()
      .append("g")
      .classed("label-g", true)
      .attr("transform", nodePos)

    sEnter
      .append("rect")
      .classed("label-bg", true)
      .attr("rx", 2)
      .attr("ry", 2)
      .attr("width", (d) => d.w)
      .attr("height", (d) => d.h)
      .style("fill", (d) => options.labelBgColor(d.data))
    sEnter
      .append("text")
      .classed("label-text", true)
      .call(updateLabelText, (d: labella.Node<HistoryEventData>) => d.data)

    const sTrans = selection.transition().attr("transform", nodePos)

    sTrans
      .select("rect")
      .attr("width", (d) => d.w)
      .attr("height", (d) => d.h)
      .style("fill", (d) => options.labelBgColor(d.data))
    sTrans
      .select("text.label-text")
      .call(updateLabelText, (d: labella.Node<HistoryEventData>) => d.data)

    selection.exit().remove()

    const paths = linkLayer.selectAll("path.link").data(nodes)
    paths
      .enter()
      .append("path")
      .classed("link", true)
      .attr("d", (d) => renderer.generatePath(d))
      .style("stroke", (d) => options.linkColor(d.data))
      .style("fill", "none")
    paths
      .transition()
      .attr("d", (d) => renderer.generatePath(d))
      .style("stroke", (d) => options.linkColor(d.data))
    paths.exit().remove()
  }

  let initialRender = true
  function render() {
    if (
      !historyData ||
      (dimension.innerWidth <= 0 && dimension.innerHeight <= 0)
    )
      return

    const data = historyData || []
    force = new labella.Force()

    scale.domain(options.domain)
    scale.range([
      0,
      options.direction === "vertical"
        ? dimension.innerHeight
        : dimension.innerWidth,
    ])

    const dummyText = dummyLayer.append("text").classed("label-text", true)
    const nodes = data.map((d: HistoryEventData) => {
      const bbox = dummyText.call(updateLabelText, d).node()!.getBBox()
      const w =
        bbox.width + options.labelPadding.left + options.labelPadding.right
      const h =
        bbox.height + options.labelPadding.top + options.labelPadding.bottom
      const node = new labella.Node(
        scale(options.timeFn(d)),
        options.direction === "vertical" ? h : w,
        d
      )
      node.w = w
      node.h = h
      return node
    })
    dummyText.remove()

    const { left, right } = options.margin
    force
      .options({ maxPos: dimension.width - (left + right) })
      .nodes(nodes)
      .compute()

    drawAxes()
    drawDots(data)
    drawLabels(force.nodes())

    if (initialRender) {
      initialRender = false
      onInitialRender()
    }
    onRender()
  }

  function onInitialRender() {
    // Customize history after initial render
    svg.insert("title", ":first-child").text(data.title)

    // Wrap labels in link tag
    labelLayer
      .selectAll<SVGGElement, labella.Node<HistoryEventData>>("g.label-g")
      .each(function () {
        const node = select<SVGGElement, labella.Node<HistoryEventData>>(this)
        const children = node.selectAll("g.label-g > *").remove()

        const anchor = node
          .append("svg:a")
          .attr("class", (d) => d.data.class)
          .attr("xlink:href", (d) => d.data.href)
          .attr("xlink:title", (d) => d.data.desc ?? null)
        anchor.append("title").text((d) => d.data.desc ?? null)

        children.each(function (this) {
          anchor.append(() => this)
        })
      })

    // Attach hover and click events
    const publicationClickHandler = widget.handlerValue("publicationClick")
    const hasClickHandler = !!publicationClickHandler
    labelLayer
      .selectAll<SVGGElement, labella.Node<HistoryEventData>>(".label-g")
      .attr("data-clickable", () => hasClickHandler || null)
      .on("click", function (event: Event, node) {
        if (publicationClickHandler) {
          publicationClickHandler(node.data.query)
          event.preventDefault()
        }
      })
      .on("mouseenter touchstart", function (event, node) {
        if (!event.target) {
          return
        }

        const coloring = getEventColors(node.data)
        // highlight label
        select(event.target).select("rect").style("fill", coloring.bright)

        // highlight link and dot
        const id = node.data.id
        linkLayer
          .selectAll<Element, labella.Node<HistoryEventData>>("path.link")
          .filter((node) => node.data.id === id)
          .attr("data-highlighted", true)

        // highlight publications in pub table
        const publicationId = node.data.query && node.data.query.id
        if (publicationId) {
          selectAll("[data-publication-id='" + publicationId + "']").style(
            "background-color",
            coloring.light
          )
        }
      })
      .on("mouseleave touchend", function (event, node) {
        if (!event.target) {
          return
        }

        const coloring = getEventColors(node.data)
        // unhighlight label
        select(event.target).select("rect").style("fill", coloring.standard)

        // unhighlight link
        linkLayer
          .selectAll<Element, labella.Node<HistoryEventData>>("path.link")
          .attr("data-highlighted", false)

        // unhighlight publication in pub table
        const publicationId = node.data.query && node.data.query.id
        if (publicationId) {
          selectAll("[data-publication-id='" + publicationId + "']").style(
            "background-color",
            null
          )
        }
      })
  }

  function onRender() {
    // Keep tick labels readable by alternating the length of the ticks
    // once the available space per tick falls below a threshold of 32px
    const ticks = axisLayer.selectAll("g.tick")
    const widthPerTick = dimension.width / ticks.size()
    if (widthPerTick <= 32) {
      ticks.each(function (_datum: unknown, i: number) {
        if (i % 2 !== 0) {
          return
        }

        const tick = select(this)
        const textNode = tick.select("text")
        const lineNode = tick.select("line")
        textNode.attr(
          "y",
          Number(textNode.attr("y")) + options.alternatingTickLength
        )
        lineNode.attr(
          "y2",
          Number(lineNode.attr("y2")) + options.alternatingTickLength
        )
      })
    }
  }

  return {
    draw: function redraw() {
      // Container may have changed dimensions
      fitToContainer()
      // Automatically update size according to label positions
      resizeToFitNodes()
      // Force another rerender
      updateDimension()
      updateDimension.flush()
    },
  }
}
